import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { forkJoin, Observable, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';

import { DateFormatter, LibValidators } from '@core/helpers';
import { NotificationsService } from '@lib/notifications';
import { TranslateService } from '@project/translate';
import { Cid, CidService, LocalStorageService } from '@project/services';
import { Procedure } from '@project/view-models';

import { RequisitionDetailsManagerService } from '../services/requisition-details-manager.service';
import { ProceduresService } from '../services/procedures.service';
import { SessionDetailsSadtService } from '../session-details-sadt/session-details-sadt.service';
import { SessionDetailsApacService } from '../session-details-apac/session-details-apac.service';

import {
  MedicalRecord,
  StoredMedicalRecord,
} from '../../../project/modules/session-details-shared/session-details-shared-medical-record/types';
import {
  defaultCid,
  defaultMedicalRecord,
} from '../../../project/modules/session-details-shared/session-details-shared-medical-record/consts';
import {
  ProcedureFormData,
  SelectedProcedureProtocol,
} from '../../../project/modules/session-details-shared/session-details-shared-medical-record/interfaces/procedures';
import { formatMedicalReferralPayload } from '../utils/medical-referral.helper';
import { formatProcedureErrorMessage } from './error-format.helper';

@Component({
  selector: 'app-session-details-procedures',
  templateUrl: './session-details-procedures.component.html',
  styleUrls: ['./session-details-procedures.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionDetailsProceduresComponent implements OnInit, OnDestroy {
  @ViewChild('formContainer') public formContainer: ElementRef;

  public form: FormGroup;
  public filteringCtrl = new FormControl();
  public filteredOptions = new ReplaySubject<{ description: string; value: string }[]>(1);
  public resetProceduresSelect$ = new Subject<void>();

  public medicalRecord: MedicalRecord = this.getInitialMedicalRecord();
  public isCidLoading: boolean[] = [];
  public isSearching = false;
  public isButtonDisabled = true;
  public isSubmitting = false;
  public requisitionID: string;
  public currentDate: string;

  private currentProcedureType: 'APAC' | 'SADT' = 'APAC';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private requisitionDetailsManagerService: RequisitionDetailsManagerService,
    private proceduresService: ProceduresService,
    private cidService: CidService,
    private localStorageService: LocalStorageService,
    private notificationService: NotificationsService,
    private cdr: ChangeDetectorRef,
    private sadtService: SessionDetailsSadtService,
    private apacService: SessionDetailsApacService,
  ) {}

  public ngOnInit(): void {
    this.initializeForm();
    this.initializeComponent();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    const requiredMsg = TranslateService.localize('validations.required');
    const maxLengthMsg = TranslateService.localize('forms.sus-guide-max-length-error');

    this.form = this.fb.group({
      patient_id: [null, [LibValidators.required(requiredMsg)]],
      diagnosis_description: [
        null,
        [LibValidators.requiredWithoutWhitespace(requiredMsg), LibValidators.maxLength(200, maxLengthMsg)],
      ],
      primary_cid_id: [null, [LibValidators.required(requiredMsg)]],
      note: [null, [LibValidators.required(requiredMsg), LibValidators.maxLength(200, maxLengthMsg)]],
      procedures: this.fb.array([]),
      requisition_id: [null],
    });
  }

  private initializeComponent(): void {
    this.loadCurrentDate();
    this.loadRouteParams();
    this.loadRequisitionDetails();
    this.loadInitialProcedures();
    this.initializeFiltering();
    this.initializeCidLoadingState();
    this.listenToCidChanges();
    this.listenToFormChanges();
  }

  private loadCurrentDate(): void {
    this.currentDate = DateFormatter.dateToString(new Date(), { format: 'YYYY-MM-DD' });
  }

  private loadRouteParams(): void {
    this.route.parent?.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.requisitionID = params.requisitionId;
      this.cdr.markForCheck();
    });
  }

  private loadRequisitionDetails(): void {
    this.requisitionDetailsManagerService.requisition$.pipe(takeUntil(this.destroy$)).subscribe((requisition) => {
      if (!requisition) return;
      this.form.patchValue({
        requisition_id: requisition.id,
        patient_id: requisition.patient.id,
      });
      this.cdr.markForCheck();
    });
  }

  private loadInitialProcedures(): void {
    this.proceduresService
      .getSADTProcedures('')
      .pipe(takeUntil(this.destroy$))
      .subscribe((procedures) => this.updateFilteredOptions(procedures));
  }

  private initializeFiltering(): void {
    this.filteringCtrl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(''),
        debounceTime(300),
        switchMap((search) => {
          this.isSearching = true;
          this.cdr.markForCheck();
          return this.proceduresService.getSADTProcedures(search);
        }),
      )
      .subscribe((procedures) => {
        this.isSearching = false;
        this.updateFilteredOptions(procedures);
        this.cdr.markForCheck();
      });
  }

  private initializeCidLoadingState(): void {
    this.isCidLoading = this.medicalRecord.cid.map(() => false);
  }

  private listenToCidChanges(): void {
    this.cidService.currentCid.pipe(takeUntil(this.destroy$)).subscribe((cid) => {
      if (!cid) return;

      this.form.patchValue({ primary_cid_id: cid.value });

      if (this.medicalRecord.cid.length > 0) {
        this.medicalRecord.cid[0].id = cid.value;
        this.medicalRecord.cid[0].name = cid.name;
      }

      this.cdr.markForCheck();
    });
  }

  private listenToFormChanges(): void {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateButtonState();
      this.cdr.markForCheck();
    });
  }

  private updateFilteredOptions(procedures: Procedure[]): void {
    const options = procedures.map(({ description, id }) => ({ description, value: id }));
    this.filteredOptions.next(options);
  }

  private updateButtonState(): void {
    this.isButtonDisabled = this.isFormInvalid();
  }

  private isFormInvalid(): boolean {
    const hasAtLeastOneCid = this.medicalRecord.cid.some((cid) => !!cid.id);
    const filledProcedures = this.getFilledProcedures();
    const hasProcedures = filledProcedures.length > 0;

    const diagDesc = this.form.get('diagnosis_description')?.value || '';
    const note = this.form.get('note')?.value || '';

    const isHeaderValid = diagDesc.trim().length > 0 && note.trim().length > 0 && this.form.get('primary_cid_id').valid;

    const areProceduresValid = filledProcedures.every((proc) => proc.valid);

    return !hasAtLeastOneCid || !hasProcedures || !isHeaderValid || !areProceduresValid;
  }

  private getFilledProcedures(): any[] {
    return this.procedures.controls.filter((control) => !!control.get('id')?.value);
  }

  private getInitialMedicalRecord(): MedicalRecord {
    return JSON.parse(JSON.stringify(defaultMedicalRecord));
  }

  public onSelectCid(index: number, cid: Cid): void {
    this.medicalRecord.cid[index].id = cid.value;
    this.medicalRecord.cid[index].name = cid.name;
    this.cidService.setCid(cid);
    this.saveDraft();
    this.updateButtonState();
    this.cdr.markForCheck();
  }

  public onClickAddCid(): void {
    this.medicalRecord.cid.push({ ...defaultCid });
    this.isCidLoading.push(false);
    this.saveDraft();
  }

  public onClickRemoveCid(index: number): void {
    this.medicalRecord.cid.splice(index, 1);
    this.saveDraft();
    this.updateButtonState();
    this.cdr.markForCheck();
  }

  public setCidLoadingStatus(index: number, isLoading: boolean): void {
    this.isCidLoading[index] = isLoading;
    this.cdr.markForCheck();
  }

  public trackByIndex(index: number): number {
    return index;
  }

  get procedures(): FormArray {
    return this.form.get('procedures') as FormArray;
  }

  private createProcedureGroup(data?: Partial<ProcedureFormData>): FormGroup {
    return this.fb.group({
      id: [data?.id || null],
      code: [data?.code || null],
      description: [data?.description || null],
      quantity: [data?.quantity || 1],
      protocolId: [data?.protocolId || null],
      type: [data?.type || null],
    });
  }

  public handleProtocolSelection(event: {
    selectedProtocols: SelectedProcedureProtocol[];
    procedureType: string;
  }): void {
    this.currentProcedureType = event.procedureType as 'APAC' | 'SADT';
    this.clearProtocolProcedures();
    this.addSelectedProtocols(event.selectedProtocols);
    this.saveDraft();
    this.updateButtonState();
    this.cdr.markForCheck();
  }

  private clearProtocolProcedures(): void {
    for (let i = this.procedures.length - 1; i >= 0; i--) {
      const isProtocol = !!this.procedures.at(i).get('protocolId')?.value;
      if (isProtocol) {
        this.procedures.removeAt(i);
      }
    }
  }

  private addSelectedProtocols(protocols: SelectedProcedureProtocol[]): void {
    protocols.forEach((protocol) => {
      const procedures = protocol.procedures || [];
      procedures.forEach((proc) => {
        this.procedures.push(this.createProcedureGroup({ ...proc, protocolId: protocol.id, type: protocol.type }));
      });
    });
  }

  public onSubmit(): void {
    if (this.isFormInvalid()) {
      this.form.markAllAsTouched();
      return;
    }

    const requests$ = this.buildMedicalReferralRequests();

    if (requests$.length === 0) return;

    this.isSubmitting = true;
    this.cdr.markForCheck();

    forkJoin(requests$)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.handleSuccess(),
        error: (err) => this.handleError(err),
      });
  }

  private saveDraft(): void {
    const draft: StoredMedicalRecord = {
      requisitionId: this.requisitionID,
      medicalRecord: this.medicalRecord,
    };
    this.localStorageService.store('requisition_data', JSON.stringify(draft));
  }

  private handleSuccess(): void {
    this.isSubmitting = false;
    this.notificationService.success({
      message: TranslateService.localize('forms.procedures-success'),
    });

    this.resetComponentState();
    this.scrollFormToTop();
    this.cdr.markForCheck();
  }

  private buildMedicalReferralRequests(): Observable<any>[] {
    const formValue = this.form.value;
    const procedures = (this.procedures.value as ProcedureFormData[]).filter((proc) => !!proc.id);
    const requests$: Observable<any>[] = [];

    const apacProcedures = procedures.filter((p) => p.type === 'APAC');
    if (apacProcedures.length > 0) {
      const payload = formatMedicalReferralPayload(formValue, apacProcedures, 'APAC');
      requests$.push(this.apacService.createApacForm(payload));
    }

    const sadtProcedures = procedures.filter((p) => p.type === 'SADT');
    if (sadtProcedures.length > 0) {
      const payload = formatMedicalReferralPayload(formValue, sadtProcedures, 'SADT');
      requests$.push(this.sadtService.createSADTForm(payload));
    }

    return requests$;
  }

  private handleError(error: any): void {
    this.isSubmitting = false;

    const rawErrorMessage =
      error?.error?.message ||
      (typeof error?.error === 'string' ? error.error : null) ||
      TranslateService.localize('forms.procedures-error');

    const formattedMessage = formatProcedureErrorMessage(rawErrorMessage);

    const isList = formattedMessage.includes('error-formatted-container');

    const duration = isList ? 10000 : undefined;
    this.notificationService.error({
      message: formattedMessage,
      durationMs: duration,
    });
    this.cdr.markForCheck();
  }

  private scrollFormToTop(): void {
    if (this.formContainer) {
      this.formContainer.nativeElement.scrollTop = 0;
    }
  }

  private resetComponentState(): void {
    const currentPatientId = this.form.get('patient_id')?.value;
    const currentRequisitionId = this.form.get('requisition_id')?.value;

    this.form.reset({
      patient_id: currentPatientId,
      requisition_id: currentRequisitionId,
    });

    this.procedures.clear();
    this.medicalRecord = this.getInitialMedicalRecord();
    this.initializeCidLoadingState();
    this.currentProcedureType = 'APAC';
    this.resetProceduresSelect$.next();
    this.localStorageService.clear('requisition_data');
    this.updateButtonState();
  }
}
